import { serializeToHtml } from 'lib-knowledge-document-structure/serialize';
import { useArticleTranslationQuery } from 'lib-knowledge-management-common/graphql';
import { useMemo } from 'react';
import { computeDiffHtml } from 'versionHistory/utils/applyDiffMarkers';

import { Suggestion } from '../utils/toSuggestion';

export type SuggestionComparison = {
  /** The draft, with the words it adds marked up. */
  newMarked: string;
  /** The article as it stands, with the words the draft drops marked up. */
  oldMarked: string;
  loading: boolean;
  /** The article the draft updates could not be read back. */
  unavailable: boolean;
};

/**
 * Version history puts the title in the diff, so a retitled article reads as a
 * change rather than silently differing above the panels.
 */
function withTitle(html: string, title: string): string {
  return `<h1 class="title">${title}</h1>${html}`;
}

/**
 * Both sides keep their element ids: they let the diff anchor an element to its
 * counterpart, and it ignores an id that differs.
 */
const includeIds = true;

/**
 * The draft against the article it updates, both marked up for the comparison.
 *
 * The two sides arrive in different shapes: an `AiDraft` is already HTML, while
 * an article stores structured content, so the article side goes through
 * `serializeToHtml` first. That means the markup around identical prose comes
 * from two different generators — expect the diff to be less exact than version
 * history's, which compares two outputs of the same one.
 *
 * Only `ARTICLE_UPDATE` drafts have anything to compare; for a new article this
 * reports `unavailable` without querying.
 */
export function useSuggestionComparison(
  suggestion: Suggestion,
): SuggestionComparison {
  const { forArticleId, languageId, title, htmlContent } = suggestion;

  const { data, loading, error } = useArticleTranslationQuery({
    variables: { id: forArticleId ?? '', languageId },
    skip: !forArticleId,
  });

  const current = data?.articleTranslation?.draft;

  const { newMarked, oldMarked } = useMemo(() => {
    if (!current) return { newMarked: '', oldMarked: '' };

    return computeDiffHtml(
      withTitle(serializeToHtml(current.content, includeIds), current.title),
      withTitle(htmlContent, title),
    );
  }, [current, htmlContent, title]);

  return {
    newMarked,
    oldMarked,
    loading: loading && !!forArticleId,
    // A draft can outlive the article it was written against, and the reviewer
    // needs to be told that rather than shown an empty panel.
    unavailable: !forArticleId || !!error || (!loading && !current),
  };
}
